// Define API_BASE_URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3800';

/**
 * Check if an email is allowed for franchisee signup
 * @param {string} email - The email address to check
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const checkEmailAllowed = async (email) => {
    try {
        const response = await fetch(`${API_BASE_URL}/signup/check-email`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
        });

        const result = await response.json();
        
        if (!response.ok) {
            throw new Error(result.message || 'Failed to check email authorization');
        }

        return result;
    } catch (error) {
        console.error('Error checking email authorization:', error);
        throw error;
    }
};

/**
 * Check if a franchisee already exists for the given email
 * @param {string} email - The email address to check
 * @returns {Promise<{success: boolean, hasExistingFranchisee: boolean, franchisee?: object}>}
 */
export const checkExistingFranchisee = async (email) => {
    try {
        const response = await fetch(`${API_BASE_URL}/signup/check-existing-franchisee`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
        });

        const result = await response.json();

        console.log('API Response from /signup/check-existing-franchisee:', result);
        
        if (!response.ok) {
            throw new Error(result.message || 'Failed to check existing franchisee');
        }

        return result;
    } catch (error) {
        console.error('Error checking existing franchisee:', error);
        throw error;
    }
};

/**
 * Complete the franchisee signup process
 * @param {string} uid - Firebase user UID
 * @param {string} email - User email
 * @param {object} franchiseeData - Franchisee data (optional, for new franchisees)
 * @returns {Promise<{success: boolean, isExisting: boolean, franchiseeId: string}>}
 */
export const completeFranchiseeSignup = async (uid, email, franchiseeData = null) => {
    try {
        const response = await fetch(`${API_BASE_URL}/signup/complete-franchisee-signup`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid, email, franchiseeData }),
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.message || 'Failed to complete franchisee signup');
        }

        return result;
    } catch (error) {
        console.error('Error completing franchisee signup:', error);
        throw error;
    }
};

/**
 * Generate a preview of the next FSE code
 * @returns {Promise<{success: boolean, fseCode: string}>}
 */
export const generateFSECode = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/signup/generate-fse-code`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.message || 'Failed to generate FSE code');
        }

        return result;
    } catch (error) {
        console.error('Error generating FSE code:', error);
        throw error;
    }
};

/**
 * Check if a user has a complete franchisee account
 * @param {string} uid - Firebase user UID
 * @returns {Promise<{hasCompleteAccount: boolean, needsSignup: boolean}>}
 */
export const checkUserFranchiseeStatus = async (uid) => {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/check-user-status`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.message || 'Failed to check user status');
        }

        return result;
    } catch (error) {
        console.error('Error checking user franchisee status:', error);
        throw error;
    }
};
